import {
  LayerOptionsV1,
  RenderFunctionV1,
} from '@volkswagen-onehub/gfa-layer-manager';
import React from 'react';
import {useFeatureService} from './use-feature-service';
import {CommonFeatureServices} from '../contexts/feature-app-environment-context';

export interface LayerCloseOptions {
  readonly submit?: boolean;
}

export interface OpenedLayerResult {
  readonly isOpen: true;
  readonly mountPoint: HTMLElement;
  readonly disclaimerScope: string;
  readonly close: (options?: LayerCloseOptions) => void;
  readonly layerOptions: LayerOptionsV1<
    Record<string, unknown>,
    undefined,
    LayerCloseOptions
  >;
  readonly closeButtonLabel: string;
  readonly usesFooterDisclaimers: boolean;
}

export interface ClosedLayerResult {
  readonly isOpen: false;
  open(): void;
}

export type LayerResult = OpenedLayerResult | ClosedLayerResult;

interface UseLayerOptions {
  type: 'alert' | 'focus';
  /** Document-unique Layer Id */
  layerId: string;
  /** Can the user close the layer by pressing Esc or clicking outside? */
  userCloseable: boolean;
  /** Do we need to render a list of disclaimers at the bottom of the layer? */
  usesFooterDisclaimers: boolean;
  /** Aria Label for potential close button */
  closeButtonLabel: string;
  /** Will be called after the layer was closed */
  onClosed?: (options: LayerCloseOptions | undefined) => void;
  /** Disables that the focus will return to where it was before the layer was opened */
  disableReturnFocus?: boolean;
}

export function useLayer(useLayerOptions: UseLayerOptions): LayerResult {
  const layerManager = useFeatureService(CommonFeatureServices.layerManager);

  // The options might change after the state was intialized.
  const hookOptionsRef = React.useRef(useLayerOptions);
  hookOptionsRef.current = useLayerOptions;

  const [layerResult, setLayerResult] = React.useState<LayerResult>({
    isOpen: false,
    open(): void {
      const {
        userCloseable,
        disableReturnFocus,
        type,
        usesFooterDisclaimers,
        closeButtonLabel,
        layerId,
      } = hookOptionsRef.current;

      const baseLayerOptions: LayerOptionsV1<
        Record<string, unknown>,
        undefined,
        LayerCloseOptions
      > = {
        userCloseable,
        disableReturnFocus,
        onClose: (_state, options) => {
          hookOptionsRef.current.onClosed?.(options);

          // On close revert back to the initial layer result,
          // i.e. `this` (the object literal defined as the initial state).
          setLayerResult(this);
        },
        id: layerId,
      };

      const render: RenderFunctionV1<undefined, LayerCloseOptions> =
        (_state, close) => (mountPoint) => {
          setLayerResult({
            isOpen: true,
            mountPoint,
            close,
            layerOptions: baseLayerOptions,
            usesFooterDisclaimers,
            closeButtonLabel,
            disclaimerScope: `${layerId}_disclaimers`,
          });
        };

      if (type === 'focus') {
        layerManager.openFocusLayer<undefined, LayerCloseOptions>(
          render,
          undefined,
          {...baseLayerOptions, size: 'A'}
        );
      } else if (type === 'alert') {
        layerManager.openAlert<undefined, LayerCloseOptions>(
          render,
          undefined,
          baseLayerOptions
        );
      }
    },
  });

  return layerResult;
}
